import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import LeadDrawer from "../components/LeadDrawer";
import {
  ALL_STATES, LEAD_SOURCES, LEAD_TYPES, REQUIREMENTS, SEGMENT_STYLE,
  canViewAI, isSuperStore, fmtDate, fmtINR,
} from "../lib/states";
import { Wallet, Flame, Star, Calendar, IndianRupee, Loader2, X, Filter } from "lucide-react";

/**
 * Order Pipeline — forecasted-revenue planner.
 * Only OPEN leads with a `potential_order_value > 0`, bucketed by next_action_date.
 *
 * Buckets:
 *   - thisWeek:   today, overdue, OR within today+7 days
 *   - thisMonth:  today+8 .. today+30
 *   - later:      today+31 onwards
 *   - noDate:     no `next_action_date` set
 */

// Group leads into 4 buckets keyed by ISO day-deltas from today.
function bucketize(leads) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const buckets = { thisWeek: [], thisMonth: [], later: [], noDate: [] };
  for (const l of leads) {
    const d = l.next_action_date;
    if (!d) { buckets.noDate.push(l); continue; }
    const due = new Date(d); due.setHours(0, 0, 0, 0);
    const deltaDays = Math.round((due - today) / 86400000);
    if (deltaDays <= 7) buckets.thisWeek.push(l);
    else if (deltaDays <= 30) buckets.thisMonth.push(l);
    else buckets.later.push(l);
  }
  return buckets;
}

const sumPv = (arr) => arr.reduce((acc, l) => acc + (Number(l.potential_order_value) || 0), 0);

const ATTRS = [
  { key: "requirement", label: "Requirement", get: (l) => l.requirement, opts: REQUIREMENTS },
  { key: "lead_type", label: "Lead Type", get: (l) => l.lead_type, opts: LEAD_TYPES },
  { key: "source", label: "Source", get: (l) => l.source, opts: ["WATI", ...LEAD_SOURCES] },
  { key: "state", label: "Stage", get: (l) => l.state, opts: ALL_STATES },
  { key: "segment", label: "AI Segment", get: (l) => l.segment, opts: Object.keys(SEGMENT_STYLE), aiOnly: true },
];

export default function OrderPipeline() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attr, setAttr] = useState("");
  const [val, setVal] = useState("");
  const [hotOnly, setHotOnly] = useState(false);
  const [aiStarOnly, setAiStarOnly] = useState(false);
  const [active, setActive] = useState(null);

  const ai = canViewAI(user);
  const visibleAttrs = ATTRS.filter((a) => !a.aiOnly || ai);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/order-pipeline");
      setLeads(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load order pipeline");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Memoised filter pipeline.
  const filtered = useMemo(() => {
    const def = ATTRS.find((a) => a.key === attr);
    return leads.filter((l) => {
      if (def && val) {
        const v = def.get(l);
        if (val === "__none__" ? v : v !== val) return false;
      }
      if (hotOnly && !l.is_hot) return false;
      if (aiStarOnly && !(ai && l.ai_suggested_hot)) return false;
      return true;
    });
  }, [leads, attr, val, hotOnly, aiStarOnly, ai]);

  const totalPv = sumPv(filtered);
  const buckets = bucketize(filtered);
  const attrDef = ATTRS.find((a) => a.key === attr);

  return (
    <div className="h-full flex flex-col bg-[#FAFAF9]">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-stone-200 shrink-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <Wallet className="h-5 w-5 text-stone-700" />
            <h1 className="font-heading text-2xl tracking-tight text-stone-900">Order Pipeline</h1>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-900 text-stone-100" data-testid="order-pipeline-total">
            <span className="text-[10px] tracking-[0.2em] uppercase text-stone-300">Forecast</span>
            <span className="text-base font-heading font-semibold tabular-nums">{fmtINR(totalPv)}</span>
            <span className="text-xs text-stone-300">· {filtered.length} {filtered.length === 1 ? "lead" : "leads"}</span>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-1">Open leads with a potential order value. Add or edit the value from any lead drawer.</p>
      </div>

      {/* Filter bar */}
      <div className="px-4 sm:px-6 py-2 border-b border-stone-200/70 shrink-0 bg-white">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-stone-400 shrink-0" />
          <select value={attr} onChange={(e) => { setAttr(e.target.value); setVal(""); }}
            data-testid="op-filter-attr"
            className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
            <option value="">Attribute…</option>
            {visibleAttrs.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          {attrDef && (
            <select value={val} onChange={(e) => setVal(e.target.value)}
              data-testid="op-filter-value"
              className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60 max-w-[200px]">
              <option value="">Value…</option>
              <option value="__none__">(not set)</option>
              {attrDef.opts.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          <span className="text-xs text-stone-400 pl-1.5 border-l border-stone-200 ml-1">Flags</span>
          <button onClick={() => setHotOnly((v) => !v)} data-testid="op-filter-hot"
            className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${hotOnly ? "bg-red-50 border-red-300 text-red-700" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            <Flame className="h-3.5 w-3.5" /> Hot Lead
          </button>
          {ai && (
            <button onClick={() => setAiStarOnly((v) => !v)} data-testid="op-filter-ai-star"
              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${aiStarOnly ? "bg-amber-50 border-amber-300 text-amber-700" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
              <Star className="h-3.5 w-3.5" /> AI Star
            </button>
          )}
          {(attr || val || hotOnly || aiStarOnly) && (
            <button onClick={() => { setAttr(""); setVal(""); setHotOnly(false); setAiStarOnly(false); }}
              data-testid="op-filter-clear"
              className="ml-auto flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto col-scroll px-4 sm:px-6 py-5">
        {loading ? (
          <div className="flex justify-center pt-16 text-stone-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-7">
            <Bucket title="This week / Overdue" subtitle="due today or within the next 7 days" icon={<Calendar className="h-4 w-4 text-red-500" />}
              accent="bg-red-50 border-red-200" onCardClick={setActive} leads={buckets.thisWeek} testid="bucket-this-week" />
            <Bucket title="This month" subtitle="due in 8 – 30 days" icon={<Calendar className="h-4 w-4 text-amber-500" />}
              accent="bg-amber-50 border-amber-200" onCardClick={setActive} leads={buckets.thisMonth} testid="bucket-this-month" />
            <Bucket title="Later" subtitle="due 30+ days from today" icon={<Calendar className="h-4 w-4 text-stone-500" />}
              accent="bg-stone-100 border-stone-300" onCardClick={setActive} leads={buckets.later} testid="bucket-later" />
            <Bucket title="No date" subtitle="next action date not set yet" icon={<Calendar className="h-4 w-4 text-stone-400" />}
              accent="bg-white border-stone-200" onCardClick={setActive} leads={buckets.noDate} testid="bucket-no-date" />
          </div>
        )}
      </div>

      {active && (
        <LeadDrawer leadId={active.id} onClose={() => setActive(null)} onSaved={load} />
      )}
    </div>
  );
}

function Bucket({ title, subtitle, icon, accent, leads, onCardClick, testid }) {
  const total = sumPv(leads);
  return (
    <section data-testid={testid}>
      <div className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 mb-2 ${accent}`}>
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <h2 className="text-sm font-semibold tracking-tight text-stone-900">{title}</h2>
            <p className="text-[11px] text-stone-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-right">
          <span className="text-xs text-stone-500">{leads.length} {leads.length === 1 ? "lead" : "leads"}</span>
          <span className="text-sm font-heading font-semibold text-stone-900 tabular-nums">{fmtINR(total)}</span>
        </div>
      </div>
      {leads.length === 0 ? (
        <p className="text-xs text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-lg">— no leads in this window —</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {leads.map((l) => <OrderRow key={l.id} l={l} onClick={() => onCardClick(l)} />)}
        </div>
      )}
    </section>
  );
}

function OrderRow({ l, onClick }) {
  return (
    <div onClick={onClick} data-testid={`op-row-${l.id}`}
      className="bg-white rounded-lg border border-stone-200 hover:border-stone-400 hover:shadow-sm transition-all p-3.5 cursor-pointer">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-sm text-stone-900 truncate">{l.name}</p>
        <div className="flex items-center gap-1 shrink-0">
          {l.is_hot && <Flame className="h-3.5 w-3.5 text-red-500" />}
          {l.ai_suggested_hot && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
        </div>
      </div>
      <div className="flex items-baseline gap-2 mt-1.5">
        <IndianRupee className="h-3.5 w-3.5 text-stone-400" />
        <span className="text-lg font-heading font-semibold text-stone-900 tabular-nums">{fmtINR(l.potential_order_value)}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-stone-500">
        <span className="px-1.5 py-0.5 rounded bg-stone-100 border border-stone-200">{l.state}</span>
        {l.requirement && <span className="px-1.5 py-0.5 rounded bg-stone-100 border border-stone-200">{l.requirement}</span>}
        {l.next_action_date && (
          <span className="flex items-center gap-1 text-stone-600"><Calendar className="h-3 w-3" /> {fmtDate(l.next_action_date)}</span>
        )}
        {l.assignee_name && <span className="text-stone-400">· {l.assignee_name}</span>}
      </div>
    </div>
  );
}
